import { Injectable } from '@angular/core';
import {
	Address as AddressV1,
	Entrance as EntranceV1,
	Floor as FloorV1,
	RealEstate as RealEstateV1,
	Street as StreetV1,
} from '@eadsysteme/ewsrealestateclient-v1';
import { SimpleRealEstate } from '../util/simpleEntities/SimpleRealEstate';
import {
	Address as AddressV2,
	Entrance as EntranceV2,
	Floor as FloorV2,
	RealEstate as RealEstateV2,
	Street as StreetV2,
} from '@eadsysteme/ewsrealestateclient-v2';
import { SimpleAddress } from '../util/simpleEntities/SimpleAddress';
import { SimpleStreet } from '../util/simpleEntities/SimpleStreet';
import { SimpleEntrance } from '../util/simpleEntities/SimpleEntrance';
import { SimpleFloor } from '../util/simpleEntities/SimpleFloor';
import { ContactPermission } from '@eadsysteme/ewsauthclient-v1';
import ContactTypeEnum = ContactPermission.ContactTypeEnum;

@Injectable({
	providedIn: 'root',
})
export class SimpleEntityMapper {
	constructor() {}

	/*  RealEstate */
	realEstateV1ToSimpleRealEstate(realEstate: RealEstateV1): SimpleRealEstate {
		if (!realEstate) {
			return null;
		}
		return {
			id: realEstate.id,
			number: realEstate.number,
			address: realEstate.address,
			area: realEstate.area,
			companyName: realEstate.companyName ?? '',
			entrances: this.entrancesV1ToSimpleEntrances(realEstate.entrances),
			propertyManagementId: realEstate.propertyManagementId ?? '',
			propertyManagementNumber: realEstate.propertyManagementNumber ?? '',
		};
	}

	realEstateV2ToSimpleRealEstate(realEstate: RealEstateV2): SimpleRealEstate {
		if (!realEstate) {
			return null;
		}
		return {
			area: realEstate.area,
			id: realEstate.id,
			number: realEstate.number,
			address: realEstate.address,
			ownerContactId: realEstate.contacts?.filter((val) => val.contactType === ContactTypeEnum.OWNER)[0]?.contactId ?? '',
			propertyManagementId: realEstate.contacts?.filter((val) => val.contactType === ContactTypeEnum.PROPERTY_MANAGEMENT)[0]?.contactId ?? '',
			propertyManagementNumber: realEstate.propertyManagementNumber ?? '',
		};
	}

	simpleRealEstateToRealEstateV1(simpleRealEstate: SimpleRealEstate): RealEstateV1 {
		if (!simpleRealEstate) {
			return null;
		}
		return {
			id: simpleRealEstate.id,
			address: this.simpleAddressToAddressV1(simpleRealEstate.address),
			number: simpleRealEstate.number,
			propertyManagementNumber: simpleRealEstate.propertyManagementNumber ?? '',
			propertyManagementId: simpleRealEstate.propertyManagementId ?? '',
			companyName: simpleRealEstate.companyName ?? '',
			area: simpleRealEstate.area,
			entrances: this.simpleEntrancesToEntrancesV1Array(simpleRealEstate.entrances),
		};
	}

	simpleRealEstateToRealEstateV2(realEstate: SimpleRealEstate): RealEstateV2 {
		if (!realEstate) {
			return null;
		}
		return {
			area: realEstate.area,
			id: realEstate.id,
			address: this.simpleAddressToAddressV2(realEstate.address),
			number: realEstate.number,
			propertyManagementNumber: realEstate.propertyManagementNumber ?? '',
			contacts: [
				{ contactId: realEstate.propertyManagementId, contactType: ContactTypeEnum.PROPERTY_MANAGEMENT },
				{ contactId: realEstate.ownerContactId, contactType: ContactTypeEnum.OWNER },
			],
		};
	}

	/*  Address */

	addressV1ToSimpleAddress(address: AddressV1): SimpleAddress {
		if (!address) {
			return null;
		}
		return {
			id: address.id,
			streets: this.streetsV1ToSimpleStreets(address.streets),
			city: address.city ?? '',
			zipCode: address.zipCode ?? '',
			postOfficeBox: address.postOfficeBox ?? '',
			district: address.district ?? '',
			country: address.country,
		};
	}

	addressV2ToSimpleAddress(address: AddressV2): SimpleAddress {
		if (!address) {
			return null;
		}
		return {
			id: address.id,
			streets: this.streetsV2ToSimpleStreets(address.streets),
			city: address.city ?? '',
			zipCode: address.zipCode ?? '',
			postOfficeBox: address.postOfficeBox ?? '',
			district: address.district ?? '',
			country: address.country,
		};
	}

	simpleAddressToAddressV1(address: SimpleAddress): AddressV1 {
		if (!address) {
			return null;
		}
		return {
			id: address.id,
			streets: this.simpleStreetsToStreetsV1(address.streets),
			zipCode: address.zipCode ?? '',
			city: address.city ?? '',
			country: address.country,
			district: address.district ?? '',
			postOfficeBox: address.postOfficeBox ?? '',
		};
	}

	simpleAddressToAddressV2(address: SimpleAddress): AddressV2 {
		if (!address) {
			return null;
		}
		return {
			id: address.id,
			streets: this.simpleStreetsToStreetsV2(address.streets),
			zipCode: address.zipCode ?? '',
			city: address.city ?? '',
			country: address.country,
			district: address.district ?? '',
			postOfficeBox: address.postOfficeBox ?? '',
		};
	}

	/*  Street    */

	simpleStreetsToStreetsV1(streets: SimpleStreet[]): StreetV1[] {
		if (!streets) {
			return [];
		}
		const streetsV1: StreetV1[] = [];
		streets.forEach((street) => {
			streetsV1.push(this.simpleStreetToStreetV1(street));
		});
		return streetsV1;
	}

	simpleStreetsToStreetsV2(streets: SimpleStreet[]): StreetV2[] {
		if (!streets) {
			return [];
		}
		const streetsV2: StreetV1[] = [];
		streets.forEach((street) => {
			streetsV2.push(this.simpleStreetToStreetV2(street));
		});
		return streetsV2;
	}

	simpleStreetToStreetV1(street: SimpleStreet): StreetV1 {
		if (!street) {
			return null;
		}
		return {
			id: street.id,
			streetName: street.streetName ?? '',
			houseNumber: street.houseNumber ?? '',
		};
	}

	simpleStreetToStreetV2(street: SimpleStreet): StreetV2 {
		if (!street) {
			return null;
		}
		return {
			id: street.id,
			streetName: street.streetName ?? '',
			houseNumber: street.houseNumber ?? '',
		};
	}

	streetV1ToSimpleStreet(street: StreetV1): SimpleStreet {
		if (!street) {
			return null;
		}
		return {
			id: street.id,
			streetName: street.streetName ?? '',
			houseNumber: street.houseNumber ?? '',
		};
	}

	streetsV1ToSimpleStreets(streets: StreetV1[]): SimpleStreet[] {
		if (!streets) {
			return [];
		}
		const streetsV1: SimpleStreet[] = [];
		streets.forEach((street) => {
			streetsV1.push(this.streetV1ToSimpleStreet(street));
		});
		return streetsV1;
	}

	streetV2ToSimpleStreet(street: StreetV2): SimpleStreet {
		if (!street) {
			return null;
		}
		return {
			id: street.id,
			houseNumber: street.houseNumber,
			streetName: street.streetName,
		};
	}

	streetsV2ToSimpleStreets(streets: StreetV2[]): SimpleStreet[] {
		if (!streets) {
			return [];
		}
		const streetsV2: SimpleStreet[] = [];
		streets.forEach((street) => {
			streetsV2.push(this.streetV2ToSimpleStreet(street));
		});
		return streetsV2;
	}

	/*  Entrance    */

	simpleEntranceToEntranceV2(simpleEntrance: SimpleEntrance): EntranceV2 {
		if (!simpleEntrance) {
			return null;
		}
		return {
			id: simpleEntrance.id,
			address: simpleEntrance.address,
			number: simpleEntrance.number,
			validTo: simpleEntrance.validTo ?? '',
			validFrom: simpleEntrance.validFrom ?? '',
			referenceId: simpleEntrance.referenceId ?? '',
			additionalInfo: simpleEntrance.additionalInfo ?? '',
		};
	}

	simpleEntrancesToEntrancesV2(simpleEntrances: Set<SimpleEntrance>): Set<EntranceV2> {
		if (!simpleEntrances) {
			return new Set();
		}
		const entrances: Set<EntranceV2> = new Set();
		simpleEntrances.forEach((entrance) => {
			entrances.add(this.simpleEntranceToEntranceV2(entrance));
		});
		return entrances;
	}

	simpleEntranceToEntranceV1(simpleEntrance: SimpleEntrance): EntranceV1 {
		if (!simpleEntrance) {
			return null;
		}
		return {
			id: simpleEntrance.id,
			address: simpleEntrance.address,
			number: simpleEntrance.number,
			additionalInfo: simpleEntrance.additionalInfo ?? '',
			floors: this.simpleEntrancesToEntrancesV1Set(simpleEntrance.floors),
			validFrom: simpleEntrance.validFrom ?? '',
			validTo: simpleEntrance.validTo ?? '',
			sharedId: simpleEntrance.sharedId ?? '',
		};
	}

	simpleEntrancesToEntrancesV1Set(simpleEntrances: Set<SimpleEntrance>): Set<EntranceV1> {
		if (!simpleEntrances) {
			return new Set();
		}
		const entrances: Set<EntranceV1> = new Set();
		simpleEntrances.forEach((entrance) => {
			entrances.add(this.simpleEntranceToEntranceV1(entrance));
		});
		return entrances;
	}

	simpleEntrancesToEntrancesV1Array(simpleEntrances: Set<SimpleEntrance>): Array<EntranceV1> {
		if (!simpleEntrances) {
			return [];
		}
		const entrances: Array<EntranceV1> = [];
		simpleEntrances.forEach((entrance) => {
			entrances.push(this.simpleEntranceToEntranceV1(entrance));
		});
		return entrances;
	}

	entranceV1ToSimpleEntrance(entrance: EntranceV1): SimpleEntrance {
		if (!entrance) {
			return null;
		}
		return {
			id: entrance.id,
			address: entrance.address,
			number: entrance.number,
			additionalInfo: entrance.additionalInfo ?? '',
			floors: this.floorsV1ToSimpleFloors(entrance.floors),
			validFrom: entrance.validFrom ?? '',
			validTo: entrance.validTo ?? '',
			sharedId: entrance.sharedId ?? '',
		};
	}

	entranceV2ToSimpleEntrance(entrance: EntranceV2): SimpleEntrance {
		if (!entrance) {
			return null;
		}
		return {
			id: entrance.id,
			address: entrance.address,
			number: entrance.number,
			referenceId: entrance.referenceId,
			validTo: entrance.validTo,
			validFrom: entrance.validFrom,
			additionalInfo: entrance.additionalInfo,
		};
	}

	entrancesV1ToSimpleEntrances(entrances: EntranceV1[]): Set<SimpleEntrance> {
		if (!entrances) {
			return new Set();
		}
		const simpleEntrances: Set<SimpleEntrance> = new Set();
		entrances.forEach((entrance) => {
			simpleEntrances.add(this.entranceV1ToSimpleEntrance(entrance));
		});
		return simpleEntrances;
	}

	entrancesV2ToSimpleEntrances(entrances: EntranceV2[]): SimpleEntrance[] {
		if (!entrances) {
			return [];
		}
		const simpleEntrances: SimpleEntrance[] = [];
		entrances.forEach((entrance) => {
			simpleEntrances.push(this.entranceV2ToSimpleEntrance(entrance));
		});
		return simpleEntrances;
	}

	/*  Floor   */
	floorV1ToSimpleFloor(floor: FloorV1): SimpleFloor {
		if (!floor) {
			return null;
		}
		return {
			id: floor.id,
			sharedId: floor.sharedId ?? '',
			number: floor.number,
			validFrom: floor.validFrom ?? '',
			validTo: floor.validTo ?? '',
			floorType: floor.floorType,
			entranceNumber: floor.entranceNumber,
		};
	}

	floorV2ToSimpleFloor(floor: FloorV2): SimpleFloor {
		if (!floor) {
			return null;
		}
		return {
			id: floor.id,
			entranceNumber: floor.number,
			floorType: floor.floorType,
			validTo: floor.validTo ?? '',
			validFrom: floor.validFrom ?? '',
			number: floor.number,
		};
	}

	floorsV1ToSimpleFloors(floors: Set<FloorV1>): Set<SimpleFloor> {
		if (!floors) {
			return new Set();
		}
		const simpleFloors: Set<SimpleFloor> = new Set();
		floors.forEach((floor) => {
			simpleFloors.add(this.floorV1ToSimpleFloor(floor));
		});
		return simpleFloors;
	}

	floorsV2ToSimpleFloors(floors: Set<FloorV2>): Set<SimpleFloor> {
		if (!floors) {
			return new Set();
		}
		const simpleFloors: Set<SimpleFloor> = new Set();
		floors.forEach((floor) => {
			simpleFloors.add(this.floorV2ToSimpleFloor(floor));
		});
		return simpleFloors;
	}

	simpleFloorToFloorV1(simpleFloor: SimpleFloor): FloorV1 {
		if (!simpleFloor) {
			return null;
		}
		return {
			id: simpleFloor.id,
			number: simpleFloor.number,
			validFrom: simpleFloor.validFrom ?? '',
			validTo: simpleFloor.validTo ?? '',
			floorType: simpleFloor.floorType,
			sharedId: simpleFloor.sharedId ?? '',
			entranceNumber: simpleFloor.entranceNumber,
		};
	}

	simpleFloorToFloorV2(simpleFloor: SimpleFloor): FloorV2 {
		if (!simpleFloor) {
			return null;
		}
		return {
			id: simpleFloor.id,
			number: simpleFloor.number,
			floorType: simpleFloor.floorType,
			validTo: simpleFloor.validTo ?? '',
			validFrom: simpleFloor.validFrom ?? '',
		};
	}

	simpleFloorsToFloorsV1(simpleFloors: Set<SimpleFloor>): Set<FloorV1> {
		if (!simpleFloors) {
			return new Set();
		}
		const floors: Set<FloorV1> = new Set();
		simpleFloors.forEach((floor) => {
			floors.add(this.simpleFloorToFloorV1(floor));
		});
		return floors;
	}

	simpleFloorsToFloorsV2(simpleFloors: Set<SimpleFloor>): Set<FloorV2> {
		if (!simpleFloors) {
			return new Set();
		}
		const floors: Set<FloorV2> = new Set();
		simpleFloors.forEach((floor) => {
			floors.add(this.simpleFloorToFloorV2(floor));
		});
		return floors;
	}
}
